import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, AlertTriangle, Car, Shield, PoundSterling, XCircle } from 'lucide-react';

export const DoubleClaimSection = () => {
  return (
    <section className="py-20 lg:py-28 bg-primary">
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-destructive/20 border border-destructive/30 mb-6">
            <AlertTriangle className="w-4 h-4 text-destructive" />
            <span className="text-sm font-semibold text-destructive">Double Scandal</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground mb-6">
            Were You Sold GAP Insurance With Your Car Finance?
          </h2>
          <p className="text-xl text-primary-foreground/80 leading-relaxed">
            You may have <span className="text-gold font-bold">two claims</span>, not one.
          </p>
        </div>

        {/* The Double Hit Explanation */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="bg-primary-foreground/10 border-none">
            <CardContent className="p-8">
              <p className="text-lg text-primary-foreground leading-relaxed mb-6">
                Many consumers who took out PCP or HP car finance were hit twice. First, dealers inflated 
                their interest rates through hidden commission arrangements — the scandal the FCA is now 
                investigating. But that wasn't enough.
              </p>
              <p className="text-lg text-primary-foreground leading-relaxed">
                At the same time, <strong className="text-gold">GAP insurance was routinely added to these already 
                mis-sold agreements</strong> — often without proper explanation, and with dealers pocketing up to 
                70% of the premium in undisclosed commission. Same tactics. Same greed. Double the harm.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Two Claims Visual */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
          <Card className="bg-primary-foreground/5 border-destructive/30 hover:border-destructive/50 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-destructive/20 flex items-center justify-center">
                  <Car className="w-6 h-6 text-destructive" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-destructive uppercase tracking-wider">Claim 1</span>
                  <h3 className="text-xl font-bold text-primary-foreground">Your Car Finance</h3>
                </div>
              </div>
              <p className="text-primary-foreground/80 mb-4">
                If discretionary commission wasn't disclosed on your PCP or HP finance, you may have paid 
                <strong className="text-primary-foreground"> hundreds or thousands more</strong> in interest than you should have.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-primary-foreground/70">
                  <XCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                  <span>Hidden commission inflated your rate</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-primary-foreground/70">
                  <XCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                  <span>You weren't told the dealer could increase it</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-primary-foreground/70">
                  <XCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                  <span>FCA estimates £7.5bn in total redress</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-primary-foreground/5 border-gold/30 hover:border-gold/50 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-gold uppercase tracking-wider">Claim 2</span>
                  <h3 className="text-xl font-bold text-primary-foreground">Your GAP Insurance</h3>
                </div>
              </div>
              <p className="text-primary-foreground/80 mb-4">
                If GAP insurance was added without proper disclosure of the commission or cheaper alternatives, 
                you may be owed a <strong className="text-primary-foreground">refund of the premium plus interest</strong>.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-primary-foreground/70">
                  <XCircle className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  <span>Commission often went undisclosed to the dealer</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-primary-foreground/70">
                  <XCircle className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  <span>Same cover available elsewhere for less</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-primary-foreground/70">
                  <XCircle className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  <span>Many providers have suspended sales</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* The Reality Check */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="bg-gold/10 border-gold/30">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <PoundSterling className="w-8 h-8 text-gold flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-primary-foreground mb-3">
                    The Reality: You May Have Overpaid Twice
                  </h3>
                  <p className="text-primary-foreground/90 leading-relaxed">
                    Picture this: You bought a car on finance. The dealer quietly increased your interest rate 
                    to boost their commission — you paid more every month without knowing why. Then they sold 
                    you GAP insurance, keeping <strong className="text-gold">a significant portion</strong> as commission while telling you 
                    it was essential protection. Two products. Two hidden commissions. One customer left out of pocket.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-lg text-primary-foreground/80 mb-6">
            Blue Lion Law can assess <strong className="text-primary-foreground">both claims together</strong> — ensuring you don't miss 
            out on any compensation you may be owed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="gold" size="lg" asChild>
              <a href="/gap-insurance-claims" data-testid="double-claim-gap-btn">
                Check GAP Insurance Claim
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
            <Button variant="ghost-light" size="lg" className="border border-primary-foreground/30" asChild>
              <a href="/pcp-car-finance-claims" data-testid="double-claim-pcp-btn">
                Check PCP Finance Claim
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
